import { makeAutoObservable } from 'mobx';

function checkedOrDefault(value, defaultValue) {
    return value == null ? defaultValue : value;
}

class SelectionController {
    checked = {};
    unChecked = {};
    pagesSelected = {};
    allSelected = false;
    data = null;
    parent = null;
    usersTotal = 0;
    idAccessor = (x) => x.id;

    constructor(data, parent, idAccessor, usersTotal) {
        makeAutoObservable(this);
        this.data = data;
        this.parent = parent;
        this.usersTotal = usersTotal;
        if (idAccessor) this.idAccessor = idAccessor;
    }

    setData(data, usersTotal) {
        this.data = data;
        this.usersTotal = usersTotal;
    }

    get effectiveUsersTotal() {
        const parentTotal = this.parent?.usersTotal;
        if (parentTotal != null && parentTotal > 0) {
            return parentTotal;
        }
        return this.usersTotal;
    }

    get pageSize() {
        return this.parent?.pageSize || 0;
    }

    setAllPagesSelected() {
        this.allSelected = true;
        this.unChecked = {};
        this.pagesSelected = {};
    }

    clearSelection() {
        this.allSelected = false;
        this.unChecked = {};
        this.checked = {};
        this.pagesSelected = {};
    }

    toggleCurrentPageSelected(checked) {
        if (!checked && this.allSelected) this.clearSelection();
        if (checked == null)
            checked = !this.pagesSelected[this.parent.pageIndex];

        this.pagesSelected[this.parent.pageIndex] = checked;
        if (!this.pagesSelected[this.parent.pageIndex]) {
            this.unChecked = {};
        } else {
            this.checked = {};
        }
    }

    toggleRow(x, checked) {
        if (
            (this.allSelected && !this.currentPageDeSelected) ||
            this.currentPageSelected
        ) {
            this.unChecked[this.idAccessor(x)] = !checked;
        } else {
            this.checked[this.idAccessor(x)] = checked;
        }
        if (this.selectionCountOnCurrentPage === 0)
            this.pagesSelected[this.parent.pageIndex] = false;

        if (this.selectionCountOnCurrentPage === this.data.length &&
            this.selectionCountOnCurrentPage > 0)
            this.toggleCurrentPageSelected(true);
    }

    get currentPageSelected() {
        return checkedOrDefault(
            this.pagesSelected[this.parent.pageIndex],
            false
        );
    }

    get currentPageDeSelected() {
        return this.pagesSelected[this.parent.pageIndex] === false;
    }

    get effectiveSelectionOnCurrentPage() {
        const result = {};
        this.data.forEach((x) => {
            result[this.idAccessor(x)] =
                (this.allSelected && !this.currentPageDeSelected) ||
                this.currentPageSelected
                    ? checkedOrDefault(
                          !this.unChecked[this.idAccessor(x)],
                          true
                      )
                    : checkedOrDefault(this.checked[this.idAccessor(x)], false);
        });

        return result;
    }

    get totalSelectionCountOnAllPages() {
        const usersTotal = this.effectiveUsersTotal;

        if (this.allSelected) {
            return Math.min(
                usersTotal -
                    Object.values(this.unChecked).filter((x) => x).length,
                usersTotal
            );
        }
        if (this.countByPages > 0) {
            let correction = 0;
            if (!this.currentPageSelected) {
                correction = this.selectionCountOnCurrentPage;
            }

            return Math.min(
                this.countByPages -
                    Object.values(this.unChecked).filter((x) => x).length +
                    correction,
                usersTotal
            );
        }

        return Math.min(
            Object.values(this.checked).filter((x) => x).length,
            usersTotal
        );
    }

    get countByPages() {
        const selectedPageCount = Object.values(this.pagesSelected).filter(
            (x) => x
        ).length;
        if (selectedPageCount === 0) return 0;

        const usersTotal = this.effectiveUsersTotal;
        const pageSize = this.pageSize;
        if (!pageSize || !usersTotal) return 0;

        let count = selectedPageCount * pageSize;

        const totalPages = Math.ceil(usersTotal / pageSize);
        const lastPageIndex = totalPages - 1;
        const lastPageSize = usersTotal - lastPageIndex * pageSize;

        if (this.pagesSelected[lastPageIndex]) {
            count = count - pageSize + lastPageSize;
        }

        return Math.min(count, usersTotal);
    }

    get haveDeselectedOnCurrentPage() {
        return (
            (this.allSelected || this.currentPageSelected) &&
            Object.values(this.effectiveSelectionOnCurrentPage).some((x) => !x)
        );
    }

    get haveDeselectedUsers() {
        return Object.values(this.unChecked).filter((x) => x).length > 0;
    }

    get selectionCountOnCurrentPage() {
        return Object.values(this.effectiveSelectionOnCurrentPage).filter(
            (x) => x
        ).length;
    }

    get selectionCountOnAllPages() {
        if (
            !this.allSelected &&
            Object.values(this.pagesSelected).filter((x) => x).length === 0 &&
            Object.values(this.checked).filter((x) => x).length === 0
        )
            return 0;
        return (
            this.effectiveUsersTotal -
            Object.values(this.unChecked).filter((x) => x).length
        );
    }

    get totalSelected() {
        const usersTotal = this.effectiveUsersTotal;
        if (this.allSelected) {
            return this.haveDeselectedUsers
                ? this.selectionCountOnAllPages
                : usersTotal;
        } else {
            return this.totalSelectionCountOnAllPages;
        }
    }

    get selectionContext() {
        return {
            allSelected: this.allSelected,
            pagesSelected: this.pagesSelected,
            unCheckedUsers: this.unChecked,
            checkedUsers: this.checked,
        };
    }
}

export default SelectionController;
